import React, { useEffect, useMemo, useState } from "react";
import { foApi, intelApi } from "../../lib/api";
import { Card, useOutreach, AddToCampaignModal, ComposeEmailModal, useCompose } from "../shared";
import { BulkSelectBar } from "../BulkSelectBar";
import { Landmark, Globe, Linkedin, UserSearch, Check, Plus, FolderPlus, Share2, Mail, ChevronDown, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SUBS = [["permanence", "Permanent capital"], ["sponsorFit", "Backs sponsors"], ["sectorFit", "Sector fit"], ["sizeFit", "Cheque fit"]];
const FIELDS = [["origin", "Whose wealth"], ["permanentCapital", "Permanent capital evidence"], ["sponsorEvidence", "Backs sponsors / searchers"],
  ["checkSize", "Cheque · EBITDA · deal size"], ["sectors", "Sector preferences"], ["holdPeriod", "Hold horizon"],
  ["structure", "Structure preferences"], ["contact", "Contact route"]];
const band = (n) => (n >= 80 ? "hot" : n >= 60 ? "warm" : n >= 40 ? "cool" : "cold");

const Office = ({ o, picked, onPick, onUseContact, onEmail, onCampaign, onPipeline, onContactsPipeline, onContactsCampaign }) => {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState(null);
  const [finding, setFinding] = useState(false);
  const [psel, setPsel] = useState({});
  const site = (o.website || "").trim();
  const siteHref = site ? (/^https?:\/\//i.test(site) ? site : `https://${site}`) : `https://www.google.com/search?q=${encodeURIComponent(o.name)}`;
  const selPeople = (people || []).filter((_, i) => psel[i]);
  const targetPeople = selPeople.length ? selPeople : (people || []);
  const allPicked = (people || []).length > 0 && selPeople.length === (people || []).length;

  const findContacts = async () => {
    setFinding(true);
    try {
      const res = await intelApi.apolloContacts({ audience: "investors", firm: o.name, website: site });
      setPeople(res.contacts || []);
      if (!res.contacts?.length) toast.error(`Apollo has no listed contacts for ${o.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Apollo lookup failed"); }
    setFinding(false);
  };

  return (
    <div className={`intel-row ${band(o.appetite || 0)}${picked ? " picked" : ""}`} data-testid={`fo-row-${o.id}`}>
      <label className="intel-tick"><input type="checkbox" checked={picked} onChange={(e) => onPick(o.id, e.target.checked)} data-testid={`fo-check-${o.id}`} /></label>
      <div className={`fit-ring ${band(o.appetite || 0)}`} style={{ "--pct": `${o.appetite || 0}%` }}><b>{o.appetite || 0}</b><span>appetite</span></div>

      <div className="intel-main">
        <div className="intel-head">
          <b className="intel-firm">{o.name}</b>
          <em className={`fo-flag ${(o.backsSponsors || "").toLowerCase()}`} data-testid={`fo-backs-${o.id}`}>{o.backsSponsors || "Unconfirmed"}</em>
          {o.hq ? <em>{o.hq}</em> : null}
          {o.checkSize ? <em>{o.checkSize}</em> : null}
          <span className="intel-links">
            <a href={siteHref} target="_blank" rel="noreferrer" title={site ? `Open ${site}` : `Search the web for ${o.name}`} data-testid={`fo-site-${o.id}`}><Globe size={12} /></a>
            <a href={`https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(o.name)}`} target="_blank" rel="noreferrer" title="Find them on LinkedIn" data-testid={`fo-li-${o.id}`}><Linkedin size={12} /></a>
            <button disabled={finding} onClick={findContacts} title={`Find contacts at ${o.name} with Apollo`} data-testid={`fo-apollo-${o.id}`}><UserSearch size={12} /></button>
          </span>
        </div>

        <div className="intel-contactline">
          {o.contactName ? <b>{o.contactName}</b> : null}
          {o.title ? <em>{o.title}</em> : null}
          {o.email ? <span>{o.email}</span> : <span className="dim">no email yet — use Apollo</span>}
        </div>

        <div className="intel-why">{o.why || o.permanentCapital}</div>
        {o.angle ? <div className="intel-angle"><Sparkles size={11} /> {o.angle}</div> : null}

        <div className="intel-subs" style={{ gridTemplateColumns: "repeat(4, minmax(90px, 160px))" }}>
          {SUBS.map(([k, label]) => (
            <span key={k} className="intel-sub"><i>{label}<u>{o.scores?.[k] ?? 0}</u></i><b style={{ "--w": `${o.scores?.[k] ?? 0}%` }} /></span>
          ))}
        </div>

        {people?.length ? (
          <div className="intel-people" data-testid={`fo-people-${o.id}`}>
            <div className="intel-people-h">
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={allPicked}
                  onChange={(e) => setPsel(e.target.checked ? Object.fromEntries(people.map((_, i) => [i, true])) : {})}
                  data-testid={`fo-people-all-${o.id}`} />
                Apollo contacts · {people.length}{selPeople.length ? ` · ${selPeople.length} selected` : ""}
              </label>
              <span className="intel-people-acts">
                <button className="btn btn-sm" onClick={() => onContactsPipeline(o, targetPeople)} data-testid={`fo-people-pipeline-${o.id}`}>
                  <Plus size={11} style={{ marginRight: 3 }} />Add {selPeople.length || "all"} to pipeline
                </button>
                <button className="btn btn-sm" disabled={!targetPeople.some((p) => p.email)} onClick={() => onContactsCampaign(o, targetPeople)} data-testid={`fo-people-campaign-${o.id}`}>
                  <Share2 size={11} style={{ marginRight: 3 }} />Add {selPeople.length || "all"} to campaign
                </button>
              </span>
            </div>
            {people.map((p, i) => (
              <div className="intel-person" key={i} data-testid={`fo-person-${o.id}-${i}`}>
                <label className="intel-person-tick"><input type="checkbox" checked={!!psel[i]} onChange={(e) => setPsel((s) => ({ ...s, [i]: e.target.checked }))} data-testid={`fo-person-check-${o.id}-${i}`} /></label>
                <b>{p.name}</b><em>{p.title || "—"}</em>
                <span className={p.email ? "" : "dim"}>{p.email || "no email found"}</span>
                <span className="intel-person-ln">{p.linkedin ? <a href={p.linkedin} target="_blank" rel="noreferrer"><Linkedin size={11} /></a> : null}</span>
                <button className="btn btn-sm" onClick={() => onUseContact(o, p)} data-testid={`fo-use-person-${o.id}-${i}`}><Check size={11} style={{ marginRight: 3 }} />Use</button>
              </div>
            ))}
          </div>
        ) : people ? <div className="intel-research-pending" data-testid={`fo-people-none-${o.id}`}>No contacts found on Apollo for {o.name}.</div> : null}

        {open ? (
          <div className="intel-detail" data-testid={`fo-detail-${o.id}`}>
            {o.risk ? <div className="intel-risk">Watch: {o.risk}</div> : null}
            <div className="intel-research">
              {FIELDS.filter(([k]) => o[k]).map(([k, label]) => (
                <div key={k}><span>{label}</span><p>{o[k]}</p></div>
              ))}
              {o.approach ? <div><span>How to approach</span><p>{o.approach}</p></div> : null}
            </div>
            {(o.sources || []).length ? (
              <div className="intel-sources">
                {o.sources.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer">{`source ${i + 1}`}<ExternalLink size={9} /></a>)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="intel-acts">
        <button className="btn btn-sm" disabled={!o.email} onClick={() => onEmail(o)} title="Compose email" data-testid={`fo-email-${o.id}`}><Mail size={12} /></button>
        <button className="btn btn-sm" disabled={!o.email} onClick={() => onCampaign(o)} title="Add to campaign" data-testid={`fo-campaign-${o.id}`}><Share2 size={12} /></button>
        <button className="btn btn-sm" onClick={() => onPipeline(o)} title="Add to capital pipeline" data-testid={`fo-pipeline-${o.id}`}><Plus size={12} /></button>
        <button className="btn btn-sm" onClick={() => setOpen((x) => !x)} title={open ? "Collapse" : "Expand"} data-testid={`fo-expand-${o.id}`}>
          <ChevronDown size={12} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s ease" }} />
        </button>
      </div>
    </div>
  );
};

export const FamilyOffices = ({ dealKey, dealName, industry, geography, raiseSize, platform }) => {
  const [ctx, setCtx] = useState({ industry: industry || "", geography: geography || "", platform: platform || "",
    raiseSize: raiseSize || 0, minCheck: 0, maxCheck: 0, notes: "", target: 30 });
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picks, setPicks] = useState({});
  const [cmap, setCmap] = useState({});
  const [folder, setFolder] = useState("");
  const [explicitOnly, setExplicitOnly] = useState(false);
  const outreach = useOutreach();
  const compose = useCompose();

  useEffect(() => { setCtx((c) => ({ ...c, industry: c.industry || industry || "", geography: c.geography || geography || "", raiseSize: c.raiseSize || raiseSize || 0 })); }, [industry, geography, raiseSize]);
  useEffect(() => {
    foApi.last({ dealKey, dealName }).then((r) => { setRun(r && r.id ? r : null); if (r?.status === "running") setBusy(true); }).catch(() => setRun(null));
  }, [dealKey, dealName]);

  useEffect(() => {
    if (!busy || !run?.id) return;
    const t = setInterval(async () => {
      try {
        const r = await foApi.run(run.id);
        setRun(r);
        if (r.status !== "running") {
          setBusy(false);
          if (r.status === "error") toast.error(r.error || "The scout stopped early — partial results are shown");
          else toast.success(`${r.offices.length} family offices profiled`);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [busy, run?.id]);

  const field = (k, v) => setCtx((c) => ({ ...c, [k]: v }));

  const scan = async () => {
    setBusy(true);
    try {
      setRun(await foApi.scan({ ...ctx, dealKey, dealName }));
      toast.success("Scouting family offices and permanent-capital vehicles…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); setBusy(false); }
  };

  const offices = useMemo(() => (run?.offices || [])
    .map((o) => ({ ...o, ...(cmap[o.id] || {}) }))
    .filter((o) => !explicitOnly || o.backsSponsors === "Explicit"), [run, cmap, explicitOnly]);
  const picked = useMemo(() => offices.filter((o) => picks[o.id]), [offices, picks]);

  const useContact = (o, p) => {
    setCmap((prev) => ({ ...prev, [o.id]: { contactName: p.name, email: p.email || o.email, title: p.title || o.title, linkedin: p.linkedin } }));
    toast.success(`${p.name} set as the contact${p.email ? ` · ${p.email}` : " — no email found"}`);
  };

  // Add specific Apollo-found people (not just the office's headline contact) to Capital Markets.
  const contactsToPipeline = async (o, people) => {
    if (!people.length) return toast.error("No contacts to add");
    const name = (folder || "").trim() || `Family offices — ${new Date().toISOString().slice(0, 10)}`;
    try {
      const res = await intelApi.toPipeline({
        audience: "investors", folder: name, dealKey, dealName: dealName || "",
        rows: people.map((p) => ({ name: p.name, firm: o.name, email: p.email || "", type: "Family Office",
          why: o.why || o.permanentCapital || "", angle: o.angle || "", fit: o.appetite || 0 })),
      });
      toast.success(`${res.added.length} contact(s) at ${o.name} saved to Capital Markets · folder “${name}”${res.skipped ? ` · ${res.skipped} already there` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save them"); }
  };
  const contactsToCampaign = (o, people) => {
    const withEmail = people.filter((p) => p.email);
    if (!withEmail.length) return toast.error("None of those contacts have an email");
    outreach.start(withEmail.map((p) => ({ name: p.name, email: p.email, firm: o.name })), `${dealName || "Deal"} — Family offices`);
  };

  const emailOne = (o) => compose.start({
    title: "Email Family Office",
    recipients: [{ name: o.contactName || o.name, email: o.email, firm: o.name }], dealKey,
    subject: `${dealName || "Acquisition opportunity"} — permanent-capital partner for a ${ctx.industry || "services"} roll-up`,
    body: `Hi ${(o.contactName || "there").split(" ")[0]},\n\n${o.angle || "I thought this might fit your mandate."}\n\n`
      + `We are an independent sponsor rolling up ${ctx.industry || "lower-middle-market services"} in ${ctx.geography || "the US"}`
      + `${ctx.platform ? ` around ${ctx.platform}` : ""}.\n\nWorth a short call?\n`,
  });

  const toPipeline = async (rows, label) => {
    const name = (folder || "").trim() || `Family offices — ${new Date().toISOString().slice(0, 10)}`;
    try {
      const res = await intelApi.toPipeline({
        audience: "investors", folder: name, dealKey, dealName: dealName || "",
        rows: rows.map((o) => ({ name: o.contactName || o.name, firm: o.name, email: o.email || "", type: "Family Office",
          why: o.why || o.permanentCapital || "", angle: o.angle || "", fit: o.appetite || 0 })),
      });
      setPicks({});
      toast.success(`${res.added.length} ${label} saved to Capital Markets · folder “${name}”${res.skipped ? ` · ${res.skipped} already there` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save them"); }
  };

  const addToCrm = async (rows) => {
    try {
      const res = await intelApi.promote({ audience: "investors", rows: rows.map((o) => ({
        firm: o.name, name: o.contactName || o.name, contact: o.contactName || "", email: o.email || "",
        type: "Family Office", thesis: o.sectors || o.permanentCapital || "", checkSize: o.checkSize || "",
        location: o.hq || "", why: o.why || "" })) });
      toast.success(`${res.added.length} added to Investors${res.skipped ? ` · ${res.skipped} already there` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add them"); }
  };

  return (
    <>
      <Card title="Family Office Scout" testid="fo-input" right={(
        <button className="btn btn-primary" onClick={scan} disabled={busy} data-testid="fo-run">
          <Landmark size={13} style={{ marginRight: 6 }} />{busy ? "Scouting…" : "Scout family offices"}
        </button>
      )}>
        <div className="intel-grid">
          <div className="field"><label>Industry</label><input className="inp inp-text" value={ctx.industry} onChange={(e) => field("industry", e.target.value)} data-testid="fo-industry" /></div>
          <div className="field"><label>Geography</label><input className="inp inp-text" value={ctx.geography} onChange={(e) => field("geography", e.target.value)} placeholder="United States" data-testid="fo-geo" /></div>
          <div className="field"><label>Equity we are raising ($)</label><input className="inp inp-text" type="number" value={ctx.raiseSize} onChange={(e) => field("raiseSize", +e.target.value)} data-testid="fo-raise" /></div>
          <div className="field"><label>Cheque min ($)</label><input className="inp inp-text" type="number" value={ctx.minCheck} onChange={(e) => field("minCheck", +e.target.value)} data-testid="fo-mincheck" /></div>
          <div className="field"><label>Cheque max ($)</label><input className="inp inp-text" type="number" value={ctx.maxCheck} onChange={(e) => field("maxCheck", +e.target.value)} data-testid="fo-maxcheck" /></div>
          <div className="field"><label>Offices to find (5–100)</label><input className="inp inp-text" type="number" min="5" max="100" value={ctx.target} onChange={(e) => field("target", Math.max(5, Math.min(100, +e.target.value || 30)))} data-testid="fo-target" /></div>
          <div className="field" style={{ gridColumn: "span 2" }}><label>Platform we already own / are buying</label>
            <input className="inp inp-text" value={ctx.platform} onChange={(e) => field("platform", e.target.value)}
              placeholder="e.g. outdoor power equipment dealership platform" data-testid="fo-platform" /></div>
        </div>
        <div className="field" style={{ marginTop: 10 }}><label>Anything else the scout should weigh</label>
          <textarea className="inp inp-text" rows={2} value={ctx.notes} onChange={(e) => field("notes", e.target.value)}
            placeholder="e.g. must back independent sponsors, permanent hold, no fund-of-funds, seller rollover friendly" data-testid="fo-notes" />
        </div>
        <div className="sig-actions">
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11.5 }}>
            <input type="checkbox" checked={explicitOnly} onChange={(e) => setExplicitOnly(e.target.checked)} data-testid="fo-explicit-only" />
            Only those with explicit evidence they back sponsors
          </label>
          {busy && run ? (
            <span className="ir-fine" data-testid="fo-progress">
              {run.phase === "scoring" ? "Scoring appetite, permanence and cheque fit…" : `Reading the web — ${run.anglesDone || 0}/${run.angles || 6} angles · ${(run.offices || []).length}/${run.target || 30} offices`}
            </span>
          ) : null}
          {run && !busy ? <span className="ir-fine" data-testid="fo-meta">Last scout {String(run.ranAt || "").slice(0, 16).replace("T", " ")} · {(run.offices || []).length} offices</span> : null}
        </div>
      </Card>

      {run?.read || (run?.playbook || []).length ? (
        <Card title="Best fit & how to approach them" style={{ marginTop: 12 }} testid="fo-read">
          {run.read ? <div className="sig-read">{run.read}</div> : null}
          {(run.playbook || []).length ? (
            <ol className="fo-playbook" data-testid="fo-playbook">
              {run.playbook.map((step, i) => <li key={i}>{step}</li>)}
            </ol>
          ) : null}
        </Card>
      ) : null}

      <Card title={`Family Offices${offices.length ? ` · ${offices.length}` : ""}`} style={{ marginTop: 12 }} testid="fo-list">
        {!offices.length
          ? <div className="docs-empty" data-testid="fo-empty">{run?.status === "error" ? run.error : busy ? "The scout is reading the web — offices land here as each angle completes." : "Nothing scouted yet — set the industry above and hit “Scout family offices”."}</div>
          : (<>
            <BulkSelectBar count={picked.length} total={offices.length} withEmail={picked.filter((o) => o.email).length}
              noun="offices" allSelected={offices.length > 0 && picked.length === offices.length}
              onSelectAll={() => setPicks(Object.fromEntries(offices.map((o) => [o.id, true])))}
              onClear={() => setPicks({})} testid="fo-bulk">
              <input className="inp inp-text" style={{ width: 210 }} value={folder} onChange={(e) => setFolder(e.target.value)}
                placeholder={`Family offices — ${new Date().toISOString().slice(0, 10)}`} data-testid="fo-folder" />
              <button className={`btn btn-sm ${picked.length ? "btn-primary" : ""}`} disabled={!picked.length} onClick={() => toPipeline(picked, "offices")} data-testid="fo-save-pipeline">
                <FolderPlus size={12} style={{ marginRight: 4 }} />Save {picked.length || ""} to Capital Markets
              </button>
              <button className="btn btn-sm" disabled={!picked.length} onClick={() => addToCrm(picked)} data-testid="fo-add-crm">
                <Plus size={12} style={{ marginRight: 4 }} />Add {picked.length || ""} to Investors
              </button>
              <button className="btn btn-sm" disabled={!picked.filter((o) => o.email).length} data-testid="fo-bulk-campaign"
                onClick={() => outreach.start(picked.filter((o) => o.email).map((o) => ({ name: o.contactName || o.name, email: o.email, firm: o.name })), `${dealName || "Deal"} — Family offices`)}>
                <Share2 size={12} style={{ marginRight: 4 }} />Add to campaign
              </button>
            </BulkSelectBar>
            {offices.map((o) => (
              <Office key={o.id} o={o} picked={!!picks[o.id]} onPick={(id, on) => setPicks((p) => ({ ...p, [id]: on }))}
                onUseContact={useContact} onEmail={emailOne} onPipeline={(x) => toPipeline([x], "office")}
                onContactsPipeline={contactsToPipeline} onContactsCampaign={contactsToCampaign}
                onCampaign={(x) => outreach.start([{ name: x.contactName || x.name, email: x.email, firm: x.name }], `${dealName || "Deal"} — Family offices`)} />
            ))}
          </>)}
      </Card>

      <AddToCampaignModal open={outreach.open} onClose={() => outreach.setOpen(false)} contacts={outreach.contacts} defaultGroupName={outreach.defaultName} source="investors" />
      <ComposeEmailModal open={compose.open} onClose={() => compose.setOpen(false)} config={compose.config} />
    </>
  );
};
